import classNames from "classnames";
import Link from "next/link";
import * as React from "react";
import Slider from "react-slick";
import { CommonHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import useAccessFromApp from "@hooks/useAccessFromApp";
import { paths } from "@libs/paths";
import { IFImg } from "@libs/utils/IFImg";
import { SliderArrow } from "./SliderArrow";
import { useSportsLiveViewing_NewsArticlesQuery } from "./__generated__/queries";
import styles from "./index.module.css";
import type { Settings } from "react-slick";

const TOPICS_ITEM_MAX_LENGTH = 10;
const TOPICS_SLIDES_TO_SHOW = 2;

//MainVisualのTopicsのスライダー
// slidesToShowの数値 > スライド数 + infinite:trueの場合に表示に不具合が出るので、
// スライド数が1枚の場合はinfinite:false
const TopicsSlideSetting = (infinite: boolean): Settings => ({
  dots: false,
  slidesToShow: TOPICS_SLIDES_TO_SHOW,
  slidesToScroll: 1,
  infinite,
  swipe: false,
  swipeToSlide: false,
  variableWidth: true,
  prevArrow: <SliderArrow type="prev" />,
  nextArrow: <SliderArrow type="next" />,
});

//MainVisualのTopicsのスライドする要素
type ItemProps = {
  id: number;
  imageUrl: string;
  imageAlt: string;
};

const TopicsItem: React.FC<ItemProps> = ({ id, imageUrl, imageAlt }) => (
  <div key={id} className={styles.topicsItem}>
    <Link href={paths.newsArticle(id.toString())}>
      <img src={imageUrl} alt={imageAlt} />
    </Link>
  </div>
);

//これまでのイベントセクションの自動再生ループでスライドする要素
const EventLoopSliderThumbnails = [
  "event_01.jpg",
  "event_02.jpg",
  "event_03.jpg",
  "event_04.jpg",
  "event_05.jpg",
  "event_06.jpg",
  "event_07.jpg",
  "event_08.jpg",
  "event_09.jpg",
  "event_10.jpg",
  "event_11.jpg",
  "event_12.jpg",
];

const SportsLiveviewing: React.FC = () => {
  const isFanstaMobileApp = useAccessFromApp();
  const { data } = useSportsLiveViewing_NewsArticlesQuery({
    variables: {
      perPage: TOPICS_ITEM_MAX_LENGTH,
    },
  });
  const topics = data?.newsArticles.nodes || [];
  const hasTopics = topics.length !== 0;

  return (
    <CommonLayout hasFooter hasLandingPagesHeader>
      <CommonHead
        title={"サッカー観戦は、お店でも面白い。"}
        description={
          "スタジアムに行けない時は手軽にお店でサッカー観戦を楽しもう！スポーツバー探し・予約ならFanstaで。サッカーの試合、エリア、チームから目的の試合を上映しているお店を見つけよう。スポーツライブビューイングを楽しめるスポーツバーを多数掲載中！"
        }
        ogImage={`${process.env.APP_HOST}/img/sports-liveviewing/ogp.jpg`}
        ogUrl={paths.sportsLiveViewing}
        canonicalUrl={paths.sportsLiveViewing}
      />
      <div
        className={classNames(
          styles.wrapper,
          isFanstaMobileApp && styles.isWevView
        )}
      >
        <section className={styles.mainVisual}>
          <div className={styles.mainVisualMovie}>
            <video
              src={IFImg("/videos/sports_liveviewing.mp4")}
              poster={IFImg("/img/sports-liveviewing/posterimage.jpg")}
              muted
              playsInline
              autoPlay
              loop
              onContextMenu={(e) => {
                e.preventDefault();
              }}
            ></video>
          </div>
          <div className={styles.mainVisualContents}>
            <div className={styles.mainVisualCopy}>
              <h1>
                <img
                  src={IFImg("/img/sports-liveviewing/main_copy.svg")}
                  alt="サッカー観戦は、お店でも面白い。"
                />
              </h1>
              <div className={styles.leadText}>
                サッカー観戦はスタジアムだけじゃない！スタジアムに行けない時でももっと手軽にお店でサッカー観戦を楽しもう！
              </div>
            </div>
            {hasTopics && (
              <div className={styles.topicsArea}>
                <div className={styles.topicsAreaTitle}>
                  <img
                    src={IFImg("/img/sports-liveviewing/title_topics.svg")}
                    alt="TOPICS"
                  />
                </div>
                <div className={styles.topicsAreaSlider}>
                  <div
                    className={classNames(
                      styles.spTopicsItemsWrapper,
                      "md:none"
                    )}
                  >
                    <div className={styles.spTopicsItemsList}>
                      {topics.map((topic) => (
                        <TopicsItem
                          id={topic.id}
                          imageUrl={topic.imageUrl}
                          imageAlt={topic.title}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="none md:block">
                    <Slider
                      {...TopicsSlideSetting(
                        topics.length >= TOPICS_SLIDES_TO_SHOW
                      )}
                    >
                      {topics.map((topic) => (
                        <TopicsItem
                          id={topic.id}
                          imageUrl={topic.imageUrl}
                          imageAlt={topic.title}
                        />
                      ))}
                    </Slider>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className={classNames(styles.contents, styles.situationArea)}>
          <div className={styles.contentsWidth}>
            <h2
              className={classNames(
                styles.contentsTitle,
                styles.contentsTitleDeco,
                styles.situation
              )}
            >
              サッカー観戦を楽しみたいけど…
            </h2>

            <ul className={styles.situationItems}>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/situation_01.svg")}
                  alt=""
                />
                <p>
                  アウェイに
                  <br className="md:none" />
                  行くのは大変
                </p>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/situation_02.svg")}
                  alt=""
                />
                <p>
                  人気の試合は
                  <br className="md:none" />
                  チケットが買えない
                </p>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/situation_03.svg")}
                  alt=""
                />
                <p>
                  スタジアムまで行く
                  <br className="md:none" />
                  時間がない
                </p>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/situation_04.svg")}
                  alt=""
                />
                <p>
                  家で一人で見るのは
                  <br className="md:none" />
                  物足りない
                </p>
              </li>
            </ul>
          </div>

          <div className={styles.situationCont}>
            <img
              className={classNames(styles.image01, "none md:block")}
              src={IFImg("/img/sports-liveviewing/situation_photo.jpg")}
              alt=""
            />
            <div className={styles.situationContBox}>
              <h3 className={styles.contentsSecondlyTitle}>
                <span>そんな時はお店でサッカー観戦を楽しもう！</span>
                お店でのスポーツライブビューイングなら、
                <br className={"none md:block"} />
                きっとプラスαの楽しみがある！
              </h3>
              <p>
                同じチームのファンが集まればお店は小さなスタジアムのような空間に。
                <br className={"none md:block"} />
                自宅では味わえない興奮がお店にはある。
                <br className={"none md:block"} />
                ファン同士で意気投合して仲間を増やすチャンスも！
              </p>
            </div>
            <img
              className={classNames(styles.image02, "none md:block")}
              src={IFImg("/img/sports-liveviewing/situation_photos.png")}
              alt=""
            />
            <img
              className={"md:none"}
              src={IFImg("/img/sports-liveviewing/situation_photos_sp.png")}
              alt=""
            />
          </div>
        </section>

        <section
          className={classNames(styles.contents, styles.liveviewingArea)}
        >
          <div className={styles.contentsWidth}>
            <h2
              className={classNames(
                styles.contentsTitle,
                styles.contentsTitleDeco,
                styles.liveviewing
              )}
            >
              スポーツライブビューイングの楽しみ方
            </h2>

            <ul className={styles.liveviewingItems}>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/liveviewing_01.jpg")}
                  alt=""
                />
                <dl className={styles.liveviewingItemsAbout}>
                  <dt>大画面の迫力！</dt>
                  <dd>
                    スポーツバーといえば迫力のある大画面！観戦環境もお店ごとにユニークな特徴があり、自宅では味わえない迫力と臨場感が楽しめる！
                  </dd>
                </dl>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/liveviewing_02.jpg")}
                  alt=""
                />
                <dl className={styles.liveviewingItemsAbout}>
                  <dt>お店によって異なるお酒・食事！</dt>
                  <dd>
                    お店で観戦するなら食事も楽しもう！いつもと違うお店をお酒や料理で選ぶのも楽しみのひとつ！
                  </dd>
                </dl>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/liveviewing_03.jpg")}
                  alt=""
                />
                <dl className={styles.liveviewingItemsAbout}>
                  <dt>家や職場の近くにも！</dt>
                  <dd>
                    スタジアムは１つだけど、スポーツバーはたくさん！その日、自分の居る場所から近いお店を探すと新しい発見があるかも！
                  </dd>
                </dl>
              </li>
            </ul>
          </div>
        </section>

        <section className={classNames(styles.contents, styles.howtoArea)}>
          <div className={styles.contentsWidth}>
            <h2
              className={classNames(
                styles.contentsTitle,
                styles.contentsTitleDeco,
                styles.howto
              )}
            >
              Fanstaなら簡単に
              <br className="md:none" />
              お店探し・予約ができる!
            </h2>

            <ul className={styles.howtoItems}>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/howto_01.svg")}
                  alt=""
                />
                <dl className={styles.howtoItemsAbout}>
                  <dt>試合日程・エリア・チームで検索</dt>
                  <dd>
                    自分にあった検索方法でお店を探そう！好きなチームがある人はチームページがおすすめ！対戦カードやエリアからも検索可能！
                  </dd>
                </dl>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/howto_02.svg")}
                  alt=""
                />
                <dl className={styles.howtoItemsAbout}>
                  <dt>お店を選ぶ</dt>
                  <dd>
                    検索結果に表示された中から気になるお店をチェック！試合の上映スケジュールや予約カレンダーでお店の情報を確認することができます。
                  </dd>
                </dl>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/howto_03.svg")}
                  alt=""
                />
                <dl className={styles.howtoItemsAbout}>
                  <dt>観戦環境を確認</dt>
                  <dd>
                    モニター・食事メニュー・お店の雰囲気・ユーザーの評価など、観戦に必要な情報やこだわり条件をチェック！自分に合ったお店を見つけよう！
                  </dd>
                </dl>
              </li>
              <li>
                <img
                  src={IFImg("/img/sports-liveviewing/howto_04.svg")}
                  alt=""
                />
                <dl className={styles.howtoItemsAbout}>
                  <dt>かんたんネット予約</dt>
                  <dd>
                    日時や人数を選択したら、アプリでかんたんネット予約！予約のリクエストが店舗に送られて、承認されたら予約完了です。
                  </dd>
                </dl>
              </li>
            </ul>
          </div>
        </section>

        <section
          className={classNames(styles.contents, styles.addFavoriteArea)}
        >
          <div
            className={classNames(
              styles.contentsWidth,
              styles.addFavoriteAreaInner
            )}
          >
            <div className={styles.addFavoriteImage}>
              <img src={IFImg("/img/sports-liveviewing/favorite.svg")} alt="" />
            </div>
            <div className={styles.addFavoriteCont}>
              <h3 className={styles.contentsSecondlyTitle}>
                <span>イベント情報やお得な情報が受け取れる！</span>
                好きなチームをお気に入りに登録！
              </h3>
              <p>
                応援しているチームがあるならば、チームをお気に入りに登録しよう！チームをお気に入りに登録すれば、ライブビューイングの上映スケジュールやイベント情報を受け取ることができます。ライブビューイングがお得に楽しめるクーポンや、お気に入り登録者限定の情報など、好きなチームをお気に入りに登録してもっとお店での観戦が楽しくなるような配信を受け取ろう！
              </p>
            </div>
          </div>
        </section>

        <section className={classNames(styles.contents, styles.eventArea)}>
          <div className={styles.contentsWidth}>
            <div className={styles.eventInner}>
              <div className={styles.headLine}>
                <h3 className={styles.contentsSecondlyTitle}>
                  <span>これまでのイベント</span>
                  Fanstaなら
                  <br className={"md:none"} />
                  多様なイベント情報を掲載中
                </h3>
                <p>
                  行きたいと思ってもキッカケがない方必見！
                  <br className={"none md:block"} />
                  Fanstaではお店のイベントも多数掲載中。もちろん初心者・お1人様大歓迎です！
                </p>
              </div>

              <div className={"none md:block"}>
                <Link
                  href={paths.instagram}
                  className={styles.buttonInstagram}
                  target="_blank"
                >
                  <span>Fansta公式インスタグラム</span>
                </Link>
              </div>
            </div>
          </div>
          <div className={styles.eventBanner}>
            <div className={styles.eventLoopSlider}>
              <div className={styles.eventLoopSliderWrap}>
                <ul className={styles.eventLoopSliderItems}>
                  {EventLoopSliderThumbnails.map((thumb, i) => {
                    return (
                      <li key={i}>
                        <img
                          src={IFImg(`/img/sports-liveviewing/${thumb}`)}
                          alt=""
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className={styles.eventLoopSliderWrap}>
                <ul className={styles.eventLoopSliderItems}>
                  {EventLoopSliderThumbnails.map((thumb, i) => {
                    return (
                      <li key={i}>
                        <img
                          src={IFImg(`/img/sports-liveviewing/${thumb}`)}
                          alt=""
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          <div className={"md:none"}>
            <Link
              href={paths.instagram}
              className={styles.buttonInstagram}
              target="_blank"
            >
              <span>Fansta公式インスタグラム</span>
            </Link>
          </div>
        </section>

        <section
          className={classNames(styles.contents, styles.appDownloadArea)}
        >
          <div
            className={classNames(
              styles.contentsWidth,
              styles.appDownloadInner
            )}
          >
            <dl className={styles.appDownloadCont}>
              <dt className={styles.fanstaLogo}>
                <img src={IFImg("/img/logo_fansta.svg")} alt="Fansta" />
              </dt>
              <dd className={styles.leadText}>
                スポーツライブビューイングを楽しめるスポーツバーを多数掲載中！スポーツバー探し・予約ならFanstaで。
              </dd>
              <dd className={styles.appDownloadButtons}>
                <Link
                  href={paths.appOneLinkForSportsLiveViewing}
                  target="_blank"
                >
                  <img
                    src={IFImg("/img/app_appstore.svg")}
                    alt="App Storeからダウンロード"
                  />
                </Link>
                <Link
                  href={paths.appOneLinkForSportsLiveViewing}
                  target="_blank"
                >
                  <img
                    src={IFImg("/img/app_googleplay.png")}
                    alt="Google Playで手に入れよう"
                  />
                </Link>
              </dd>
            </dl>
            <img
              className={styles.mock}
              src={IFImg("/img/sports-liveviewing/mock.png")}
              alt=""
            />
          </div>
        </section>

        <section className={classNames(styles.contents, styles.offerArea)}>
          <div className={classNames(styles.contentsWidth, styles.offerInner)}>
            <div className={styles.headLine}>
              <h2 className={styles.contentsSecondlyTitle}>
                <span>お店の方へ</span>
                掲載・資料請求
              </h2>
              <p>
                Fanstaは、スポーツ観戦を楽しみたいファンを集客できるお店でのスポーツ観戦に特化したDAZN公認の新しい集客サービスです。掲載・資料請求をご希望の店舗様は以下からお問い合わせください。
              </p>
              <Link
                href={paths.aboutShop}
                className={styles.offerButton}
                target="_blank"
              >
                掲載のご相談はこちら
              </Link>
            </div>
            <img
              className={styles.offerImage}
              src={IFImg("/img/sports-liveviewing/offer.png")}
              alt=""
            />
          </div>
        </section>
      </div>
    </CommonLayout>
  );
};

export default SportsLiveviewing;
